"use client";

import { useEffect, useRef } from "react";
import ScriptWithConsent from "@/app/components/ui/cookies/script-with-consent";
import type { TenantAnalyticsConfigT } from "@/app/config/tenant-registry";
import { useConsentStore } from "@/app/stores/consent-store";
import type { ConsentPreferencesT } from "@/app/types/consent";
import {
  buildConsentModeDefaultScript,
  buildGoogleTagManagerBootstrapScript,
  buildGtagBootstrapScript,
  buildGtagScriptSrc,
  pushConsentUpdate,
} from "@/app/utils/helpers/analytics/google-tag-manager-bootstrap";

type Props = {
  analytics?: TenantAnalyticsConfigT;
};

const CONSENT_KEYS: (keyof ConsentPreferencesT)[] = [
  "functional",
  "analytics",
  "marketing",
];

const samePreferences = (a: ConsentPreferencesT, b: ConsentPreferencesT) =>
  CONSENT_KEYS.every((key) => a[key] === b[key]);

// Mounts the tenant's analytics loader. Scripts are only injected after
// analytics consent (ScriptWithConsent); GTM additionally receives Consent
// Mode v2 signals so tags configured by third parties can respect the
// marketing/functional categories.
export default function TenantAnalyticsScripts({ analytics }: Props) {
  const consent = useConsentStore((state) => state.consent);
  const gtmContainerId = analytics?.gtmContainerId;
  const gaMeasurementId = analytics?.gaMeasurementId;

  // The initial state is delivered via the consent default script; only later
  // changes are forwarded as consent updates (revocations trigger a reload).
  const lastPushedPreferences = useRef<ConsentPreferencesT | null>(null);

  useEffect(() => {
    if (!gtmContainerId || !consent?.preferences.analytics) return;

    const previous = lastPushedPreferences.current;
    if (!previous) {
      lastPushedPreferences.current = consent.preferences;
      return;
    }

    if (samePreferences(previous, consent.preferences)) return;

    lastPushedPreferences.current = consent.preferences;
    pushConsentUpdate(consent.preferences);
  }, [gtmContainerId, consent]);

  if (!consent) return null;

  if (gtmContainerId) {
    return (
      <>
        {/* Google Tag Manager - nur bei Analytics-Consent. Kein noscript-iframe, da es ohne Consent laden wuerde. */}
        <ScriptWithConsent
          id="gtm-consent-default"
          category="analytics"
          dangerouslySetInnerHTML={{
            __html: buildConsentModeDefaultScript(consent.preferences),
          }}
        />
        <ScriptWithConsent
          id="google-tag-manager"
          category="analytics"
          dangerouslySetInnerHTML={{
            __html: buildGoogleTagManagerBootstrapScript(gtmContainerId),
          }}
        />
      </>
    );
  }

  if (gaMeasurementId) {
    return (
      <>
        {/* Google Analytics (gtag.js) - nur bei Analytics-Consent */}
        <ScriptWithConsent
          id="google-analytics"
          category="analytics"
          async
          src={buildGtagScriptSrc(gaMeasurementId)}
        />
        <ScriptWithConsent
          id="google-analytics-config"
          category="analytics"
          dangerouslySetInnerHTML={{
            __html: buildGtagBootstrapScript(gaMeasurementId),
          }}
        />
      </>
    );
  }

  return null;
}
